import { Controller } from '@hotwired/stimulus'

// NProgress and Rails are loaded globally via Sprockets.
declare const NProgress: { start(): void; done(): void }
declare const Rails: { fire(element: Element, eventName: string): boolean }

/**
 * ImportMethodSelectorComponent controller
 *
 * Handles the simple interactive behaviors previously delegated via jQuery
 * in elections.voter_list.coffee:
 *
 *  - .upload_button       click  → NProgress.start + Rails UJS form submit
 *  - .import_button       click  → NProgress.start + label swap + UJS submit
 *  - .parse_data_option   change → UJS form submit
 *  - .import_voter_list   click  → NProgress.start  (cosmetic)
 *  - .validate_voter_list click  → NProgress.start  (cosmetic)
 *  - .start_over_mapper   click  → NProgress.start  (cosmetic)
 *  - .start_over_voter_list click → NProgress.start (cosmetic)
 *  - .start_over_mapper / .start_over_voter_list confirm:complete → NProgress.done
 *  - #duplicate_wizard_path     click → modal without click-close (jQuery)
 *  - #submit_wizard_duplication click → hide #error_div, disable button
 *
 * All listeners use delegated attachment on this.element so they survive
 * inner-DOM replacements made by Rails UJS .js.erb responses.
 *
 * NOT handled here (still in elections.voter_list.coffee, Phase 2):
 *   The import-method switching state machine on .toggle_import_option.
 */
export default class extends Controller {
  connect(): void {
    this.element.addEventListener('click', this.handleClick)
    this.element.addEventListener('change', this.handleChange)
    this.element.addEventListener('confirm:complete', this.handleConfirmComplete)
  }

  disconnect(): void {
    this.element.removeEventListener('click', this.handleClick)
    this.element.removeEventListener('change', this.handleChange)
    this.element.removeEventListener('confirm:complete', this.handleConfirmComplete)
  }

  // ---------------------------------------------------------------------------
  // Delegated handlers
  // ---------------------------------------------------------------------------

  private handleClick = (event: Event): void => {
    const target = event.target as HTMLElement

    // CSV upload button: NProgress + Rails UJS submit
    const uploadButton = target.closest<HTMLElement>('.upload_button')
    if (uploadButton) {
      event.preventDefault()
      NProgress.start()
      const form = uploadButton.closest<HTMLFormElement>('form')
      if (form) Rails.fire(form, 'submit')
      return
    }

    // "Import from prior election" button: NProgress + in-progress label + submit
    const importButton = target.closest<HTMLElement>('.import_button')
    if (importButton) {
      event.preventDefault()
      NProgress.start()
      const span = importButton.querySelector('span')
      if (span) span.textContent = 'Importing...'
      const form = importButton.closest<HTMLFormElement>('form')
      if (form) Rails.fire(form, 'submit')
      return
    }

    // Voter list action buttons: cosmetic NProgress.start
    if (target.closest('.import_voter_list, .validate_voter_list, .start_over_mapper, .start_over_voter_list')) {
      NProgress.start()
      return
    }

    // Duplicate-voter wizard modal — open without allowing backdrop-click close
    const wizardLink = target.closest<HTMLElement>('#duplicate_wizard_path')
    if (wizardLink) {
      event.preventDefault()
      const $ = (window as { jQuery?: (el: Element) => { modal(opts: object): void } }).jQuery
      if ($) $(event.target as Element).modal({ clickClose: false })
      return
    }

    // Wizard submit: hide error banner + disable the submit button
    const wizardSubmit = target.closest<HTMLElement>('#submit_wizard_duplication')
    if (wizardSubmit) {
      document.getElementById('error_div')?.classList.add('hide')
      wizardSubmit.classList.add('disabled')
    }
  }

  private handleChange = (event: Event): void => {
    const target = event.target as HTMLElement

    // Column-mapper parse-option radios: submit enclosing form
    const parseOption = target.closest<HTMLElement>('.parse_data_option')
    if (parseOption) {
      event.preventDefault()
      const form = parseOption.closest<HTMLFormElement>('form')
      if (form) Rails.fire(form, 'submit')
    }
  }

  private handleConfirmComplete = (event: Event): void => {
    const target = event.target as HTMLElement
    if (target.closest('.start_over_mapper, .start_over_voter_list')) {
      NProgress.done()
    }
  }
}
